import { SxProps, Theme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { t } from 'i18next';
import { type Key, useState } from 'react';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import { SelectOption } from '~components/FormFields/Parts/SelectOption';
import PricingEventsTimeline from '~components/Job/LoadPricing/PricingEventsTimeline';
import { FeatureFlags } from '~constants/featureFlags';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import useJobLoadsPricingQuery from '~queries/useJobLoadsPricingQuery';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

// Sentinels for the "whole order" and "whole job" options — distinct from any real id.
const ORDER_VIEW = '__order__';
const ALL_LOADS = '__job__';

// A type alias (not an interface) so it satisfies SelectOption's `Record<string, unknown>` generic
// constraint — interfaces lack the implicit index signature that constraint requires.
type PickerOption = {
  id: string;
  label: string;
  // Optional Autocomplete group header (e.g. "Individual jobs"). Ungrouped options (like the
  // whole-order option) omit it and render above the first group with no header.
  group?: string;
};

interface HistorySelectProps {
  ariaLabel: string;
  label: string;
  onChange: (id: string) => void;
  options: PickerOption[];
  value: string;
  // Styling for the field wrapper — callers set the width (e.g. `{ width: 320 }`).
  sx?: SxProps<Theme>;
  // When provided, the list is grouped by this key (the job picker). Omitted for a flat list.
  groupBy?: (option: PickerOption) => string;
}

/**
 * Single-select dropdown styled to match the order form's account/material pickers: a MUI
 * Autocomplete rendering the shared SelectOption (treadYellow accent, 48px rows, type-to-filter).
 * Always has a selection, so the clear button is disabled. Used for both the job and load pickers.
 */
function HistorySelect({
  ariaLabel,
  groupBy,
  label,
  onChange,
  options,
  sx,
  value,
}: HistorySelectProps) {
  // A real value is always present (both lists start with a sentinel option), so the fallback to
  // options[0] is only here to satisfy disableClearable's non-null value type.
  const selectedOption = options.find((option) => option.id === value) ?? options[0];

  return (
    <Box sx={sx}>
      <FormFieldLabel label={label} />

      <Autocomplete
        disableClearable
        fullWidth
        getOptionLabel={(option) => option.label}
        groupBy={groupBy}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        onChange={(_event, option) => onChange(option.id)}
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            slotProps={{
              htmlInput: { ...params.inputProps, 'aria-label': ariaLabel },
            }}
            size="small"
          />
        )}
        renderOption={(props, option, state) => {
          // MUI v6 folds `key` into the option props; spreading it onto an element triggers React's
          // unique-key warning, so lift it out and pass it explicitly.
          const { key, ...optionProps } = props;

          return (
            <SelectOption
              key={key as Key}
              getLabel={(item) => item.label}
              inputValue={state.inputValue}
              isCheckEnabled={false}
              option={option}
              props={optionProps}
              selected={state.selected}
            />
          );
        }}
        size="small"
        slotProps={{
          popper: {
            sx: (theme) => ({
              // The panel sits at zIndex.modal + 1, so the popover must be lifted above it or the
              // list opens hidden behind the panel.
              zIndex: theme.zIndex.modal + 2,
              // The whole-order option is ungrouped; hide its empty group header.
              '& .MuiAutocomplete-groupLabel:empty': { display: 'none' },
            }),
          },
        }}
        value={selectedOption}
      />
    </Box>
  );
}

interface PricingEventsHistoryProps {
  order: Order;
  jobs: Job[];
}

/**
 * Rate-history body for one Order. A job picker defaults to the whole order (order-grain timeline)
 * and can narrow to a single job (job-grain). Picking a job reveals a load picker that can narrow
 * further to a single load (load-grain). Backed by `pricing_events`; rendered in PricingHistoryPanel.
 */
export default function PricingEventsHistory({ order, jobs }: PricingEventsHistoryProps) {
  const loadLevelPricingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.loadLevelPricing,
  });

  const [selectedJobId, setSelectedJobId] = useState<string>(ORDER_VIEW);
  const [selectedLoadId, setSelectedLoadId] = useState<string>(ALL_LOADS);

  const isOrderView = selectedJobId === ORDER_VIEW;
  // Drilling into a single load is gated on load_level_pricing.
  const showLoadPicker = !isOrderView && loadLevelPricingEnabled;
  const isLoadView = showLoadPicker && selectedLoadId !== ALL_LOADS;

  // Only fetch loads once a specific job is picked and the flag is on — the picker is hidden otherwise.
  const { data: loads } = useJobLoadsPricingQuery(selectedJobId, {
    enabled: showLoadPicker,
  });

  const jobOptions: PickerOption[] = [
    { id: ORDER_VIEW, label: t('loads.pricing.history.all_jobs') },
    ...jobs.map((job) => {
      // Prefer the driver's name; fall back to the vendor account (vendor-assigned jobs have no
      // driver yet), matching the dispatch table's Driver & Vendor column.
      const assignee =
        job.driver?.fullName ?? job.vendorJobAssignment?.vendorAccount?.name;

      return {
        id: job.id,
        label: assignee ? `${job.jobId} · ${assignee}` : job.jobId,
        group: t('loads.pricing.history.individual_jobs'),
      };
    }),
  ];

  const loadOptions: PickerOption[] = [
    { id: ALL_LOADS, label: t('loads.pricing.history.all_loads') },
    ...(loads ?? []).map((load, index) => ({
      id: load.id,
      label: `${t('status.load')} ${index + 1} (${load.loadId})`,
    })),
  ];

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, pt: 1 }}>
        <HistorySelect
          ariaLabel={t('loads.pricing.history.show_for')}
          groupBy={(option) => option.group ?? ''}
          label={t('loads.pricing.history.show_for')}
          onChange={(id) => {
            setSelectedJobId(id);
            setSelectedLoadId(ALL_LOADS);
          }}
          options={jobOptions}
          sx={{ width: 320 }}
          value={selectedJobId}
        />

        {showLoadPicker && (
          <HistorySelect
            ariaLabel={t('status.load')}
            label={t('status.load')}
            onChange={setSelectedLoadId}
            options={loadOptions}
            sx={{ width: 240 }}
            value={selectedLoadId}
          />
        )}
      </Box>

      <PricingEventsTimeline
        jobId={!isOrderView && !isLoadView ? selectedJobId : undefined}
        orderId={isOrderView ? order.id : undefined}
        subjectId={isLoadView ? selectedLoadId : undefined}
        subjectType={isLoadView ? 'Load' : undefined}
      />
    </>
  );
}
